// providers/utils.ts
/**
 * @fileoverview Shared utility functions for ETL SettingsView components.
 *
 * These helpers are used across MainSettingsGroup, TagSettingsGroup, and
 * KeyFactSettingsGroup to work with provider configurations and settings.
 */

import type * as Api from '@ViewModels';
import type { ComboboxItem, SettingsField, VisibleWhenCondition } from './types';

// ============================================================================
// Record/Object Conversion
// ============================================================================

/**
 * Convert a Record<string, string> to an array of ComboboxItem.
 * Used to transform API response maps into selectable dropdown options.
 *
 * @param record - Object with id keys and label values
 * @returns Array of { id, label } items
 *
 * @example
 * ```typescript
 * const fields = { 'email': 'Email Address', 'phone': 'Phone Number' };
 * recordToItems(fields);
 * // => [{ id: 'email', label: 'Email Address' }, { id: 'phone', label: 'Phone Number' }]
 * ```
 */
export function recordToItems(record: Record<string, string> = {}): ComboboxItem[] {
	return Object.entries(record).map(([id, label]) => ({ id, label }));
}

/**
 * Return the members of `all` that are not in `exclude`, preserving the order of `all`.
 *
 * Used to invert selections for `complementSelect` fields: the selected set shown to the user is
 * `excludeValues(allOptions, storedComplement)`, and the complement persisted on save is
 * `excludeValues(allOptions, selected)`. Because the complement is always taken against the current
 * option set, stored values that are no longer present are dropped.
 *
 * @param all - The full set of values (e.g. the available options)
 * @param exclude - Values to remove from `all` (e.g. the blocked or selected values)
 * @returns Members of `all` not present in `exclude`
 *
 * @example
 * ```typescript
 * excludeValues(['Active Client', 'Deceased', 'Inactive Client'], ['Deceased', 'Inactive Client']);
 * // => ['Active Client']
 * ```
 */
export function excludeValues(all: string[], exclude: string[] = []): string[] {
	return all.filter(value => !exclude.includes(value));
}

// ============================================================================
// Configuration Access
// ============================================================================

/**
 * Get a value from a configuration object by path.
 *
 * @param configuration - The integration configuration object
 * @param path - Property name to access (e.g., 'availableCustomFields')
 * @returns The value at the path, or undefined if not found
 *
 * @example
 * ```typescript
 * const fields = getConfigValue<Record<string, string>>(configuration, 'availableCustomFields');
 * ```
 */
export function getConfigValue<T>(configuration: Api.IIntegrationConfiguration, path: string): T | undefined {
	return (configuration as unknown as Record<string, unknown>)[path] as T | undefined;
}

/**
 * Get available fields from configuration by path.
 * Convenience wrapper around getConfigValue for Record<string, string> fields.
 *
 * @param configuration - The integration configuration object
 * @param availableFrom - Path to the available fields (e.g., 'availableCustomFields')
 * @returns Record of field id -> label, or empty object if not found
 */
export function getAvailableFields(
	configuration: Api.IIntegrationConfiguration,
	availableFrom: string
): Record<string, string> {
	return getConfigValue<Record<string, string>>(configuration, availableFrom) ?? {};
}

/**
 * Combine all available field sources from configuration.
 * Merges availableFields, availableCustomFields, availableDateFields, and availableCustomDateFields.
 *
 * Used for field selectors that need access to all field types (e.g., Salesforce deceased indicator).
 *
 * @param configuration - The integration configuration object
 * @returns Combined record of all available fields
 */
export function getAllAvailableFields(configuration: Api.IIntegrationConfiguration): Record<string, string> {
	const fieldSources = ['availableFields', 'availableCustomFields', 'availableDateFields', 'availableCustomDateFields'];
	return fieldSources.reduce(
		(acc, source) => {
			const fields = getConfigValue<Record<string, string>>(configuration, source);
			return fields ? { ...acc, ...fields } : acc;
		},
		{} as Record<string, string>
	);
}

// ============================================================================
// Settings Access
// ============================================================================

/**
 * Get a value from a settings object by key.
 * Works with IIntegrationSettings, ITagSettings, or IKeyFactSettings.
 *
 * @param settings - The settings object
 * @param key - Property name to access
 * @returns The value at the key, or undefined if not found
 *
 * @example
 * ```typescript
 * const enabled = getSettingValue<boolean>(tagSettings, 'stateEnabled');
 * const fields = getSettingValue<string[]>(keyFactSettings, 'enabledDateFields');
 * ```
 */
export function getSettingValue<T>(
	settings: Api.IIntegrationSettings | Api.ITagSettings | Api.IKeyFactSettings,
	key: string
): T | undefined {
	return (settings as unknown as Record<string, unknown>)[key] as T | undefined;
}

// ============================================================================
// Nested Field Utilities
// ============================================================================

type SettingsObject = Api.IIntegrationSettings | Api.ITagSettings | Api.IKeyFactSettings;

/**
 * Get a field's value from settings, supporting nested keys via parentKey.
 *
 * @example
 * ```typescript
 * // Flat: settings.ownershipRole
 * getFieldValue(settings, { key: 'ownershipRole' })
 *
 * // Nested: settings.prospectOptions.prospectIntegration
 * getFieldValue(settings, { key: 'prospectIntegration', parentKey: 'prospectOptions' })
 * ```
 */
export function getFieldValue<T>(
	settings: SettingsObject,
	field: Pick<SettingsField, 'key' | 'parentKey'>
): T | undefined {
	if (field.parentKey) {
		const parent = getSettingValue<Record<string, unknown>>(settings, field.parentKey);
		return parent?.[field.key] as T | undefined;
	}
	return getSettingValue<T>(settings, field.key);
}

/**
 * Create an update object for a field, supporting nested keys via parentKey.
 * When parentKey is set, preserves sibling properties in the parent object.
 *
 * @example
 * ```typescript
 * // Flat: { ownershipRole: 'Primary' }
 * createFieldUpdate(settings, { key: 'ownershipRole' }, 'Primary')
 *
 * // Nested: { prospectOptions: { ...existing, prospectIntegration: 'Timeline' } }
 * createFieldUpdate(settings, { key: 'prospectIntegration', parentKey: 'prospectOptions' }, 'Timeline')
 * ```
 */
export function createFieldUpdate(
	settings: SettingsObject,
	field: Pick<SettingsField, 'key' | 'parentKey'>,
	value: unknown
): Record<string, unknown> {
	if (field.parentKey) {
		const parent = getSettingValue<Record<string, unknown>>(settings, field.parentKey) ?? {};
		return { [field.parentKey]: { ...parent, [field.key]: value } };
	}
	return { [field.key]: value };
}

/**
 * Check if a field should be visible based on its visibleWhen condition.
 */
export function isFieldVisible(settings: SettingsObject, visibleWhen?: VisibleWhenCondition): boolean {
	if (!visibleWhen) return true;
	const actualValue = visibleWhen.parentKey
		? getFieldValue(settings, { key: visibleWhen.key, parentKey: visibleWhen.parentKey })
		: getSettingValue(settings, visibleWhen.key);
	return actualValue === visibleWhen.value;
}

// ============================================================================
// Display Value Formatting
// ============================================================================

/**
 * Get display text for a list of selected field IDs.
 *
 * @param selectedIds - Array of selected field IDs
 * @param availableFields - Record mapping IDs to labels
 * @returns Comma-separated labels, or 'None selected' if empty
 *
 * @example
 * ```typescript
 * getFieldsDisplayValue(['email', 'phone'], { email: 'Email', phone: 'Phone' });
 * // => 'Email, Phone'
 * ```
 */
export function getFieldsDisplayValue(
	selectedIds: string[] = [],
	availableFields: Record<string, string> = {}
): string {
	if (!selectedIds?.length) return 'None selected';
	return selectedIds.map(id => availableFields[id] || id).join(', ');
}

/**
 * Get display text for a single selected value.
 *
 * @param value - The selected value
 * @param options - Array of options to search for the label
 * @returns The label if found, otherwise the value itself or 'None selected'
 */
export function getSelectDisplayValue(value: string | undefined, options: ComboboxItem[]): string {
	if (!value) return 'None selected';
	const option = options.find(o => o.id === value);
	return option?.label || value;
}

// ============================================================================
// Field Options Resolution
// ============================================================================

/**
 * Options for getFieldOptions function.
 */
export interface GetFieldOptionsConfig {
	/** The integration configuration object */
	configuration: Api.IIntegrationConfiguration;
}

/**
 * Get options for a field based on optionsFrom path.
 * Handles special paths like 'allAvailableFields'.
 *
 * @param optionsFrom - Path to options in configuration
 * @param config - Configuration object
 * @returns Array of ComboboxItem options
 *
 * @example
 * ```typescript
 * // Standard configuration path
 * getFieldOptions('availableRecordTypes', { configuration });
 *
 * // Combined fields
 * getFieldOptions('allAvailableFields', { configuration });
 * ```
 */
export function getFieldOptions(optionsFrom: string, config: GetFieldOptionsConfig): ComboboxItem[] {
	const { configuration } = config;

	// Handle special 'allAvailableFields' marker
	if (optionsFrom === 'allAvailableFields') {
		return recordToItems(getAllAvailableFields(configuration));
	}

	// Get from configuration
	const configValue = getConfigValue<Record<string, string> | string[]>(configuration, optionsFrom);
	if (configValue && typeof configValue === 'object') {
		// Handle string[] arrays (e.g., availableRecordTypes) by using the value as both id and label
		if (Array.isArray(configValue)) {
			return configValue.map(value => ({ id: value, label: value }));
		}
		return recordToItems(configValue);
	}

	return [];
}
